import { SeriesPointXY } from '../Types';

const axis0 = {
  x: 'x0',
  y: 'y0',
};

interface Options {
  axis: 'x' | 'y';
  domain?: number[];
  overlap?: boolean;
}

/**
 * A utility function that accepts multiple series in order or adjusting
 * them to become a stacked series.
 *
 * @param series Multiple series of data point objects, with X and Y values
 * that require stacking.
 *
 * @param axis The X or Y axis to be stacked.
 * @default "y"
 *
 * @param domain Current lower and upper bounds that are to be used for
 * calculating the lowest connectors for null values.
 *
 * @returns An array with 2 values. The first, the adjusted stacked series. Note
 * this will be reversed from the order that was given so that the series with the
 * lowest value is the first index. The second element is the new adjust yDomain
 * bounds.
 */
const getStackedSeries = (series: SeriesPointXY[][], options: Options): [SeriesPointXY[][], number[]] => {
  const { axis, domain = [], overlap } = options;
  const seriesStacked: SeriesPointXY[][] = Array.from(series).map(() => []);
  let lower = domain[0];
  let upper = domain[domain.length - 1];

  if (lower === undefined) {
    for (const s of series) {
      for (const p of s) {
        const v = p[axis];

        if (v !== null && (lower === undefined || v < lower)) {
          lower = v;
        }
      }
    }
  }

  if (lower !== undefined) {
    for (let i = 0; i < series.length; i++) {
      const s = series[i];
      const ss = seriesStacked[i];
      const sp = seriesStacked[i - 1];

      for (let j = 0; j < s.length; j++) {
        const p = s[j];
        const pp = sp && sp[j];

        const v = p[axis] || 0;
        const v0 = pp && pp[axis] || lower;
        const vs = v0 + v;

        if (upper === undefined || vs > upper) {
          upper = vs;
        }

        ss.push({
          x: p.x,
          y: p.y,
          [axis]: vs,
          [axis0[axis]]: overlap ? lower : v0,
        });
      }
    }
  }

  if (lower === undefined || upper === undefined) {
    return [[], []];
  }

  return [
    seriesStacked,
    [lower, upper],
  ];
};


export default getStackedSeries;
